/**
 * keyHole 类型策略：钥匙扣（外圆 + 内圆，单实例上限 1）。
 *
 * 几何：
 *  - outRadius / inRadius = (直径/2) * mmToPixelRatio / maskFitScale
 *  - centerPoint = canvas 坐标
 *
 * 拖动约束（spec §3.3，与旧 serviceHoleNew/interaction_api_public.js#handleTouchMove 行为一致）：
 *  - 圆心在轮廓内：距最近轮廓点 ≤ outRadius（外圆与轮廓相切）
 *  - 圆心在轮廓外：距最近轮廓点 ≤ inRadius（内圆与轮廓相切）
 *
 * 导出：
 *  - keyPointList:           [[x,y]] 比例 0~1
 *  - originCenterPointList:  { x, y, baseCanvasWidth, componentId, geometryConfig, sizePresetId?, enableMagnifier? }
 */

import { POSITION } from '@/utils/constant.js';

import {
  convertCanvasToBackend,
  findClosestPoint,
  getRestoreBusinessScale,
  isPointInPolygon
} from '../core.js';
import {
  DRAW_TYPE,
  lineColor,
  roundColor,
  DEFAULT_CONFIG
} from '../constants.js';
import { registerComponentType } from '../typeRegistry.js';

/**
 * 校验 keyHole 的 geometryConfig。
 * @param {Object} g
 * @returns {string|null}
 */
function validateGeometryConfig(g) {
  if (!g) return '缺少组件尺寸配置：geometryConfig';
  if (typeof g.outerHoleDiameterMm !== 'number' || g.outerHoleDiameterMm <= 0) {
    return '缺少或非法字段：outerHoleDiameterMm（必须 > 0 的数字）';
  }
  if (typeof g.innerHoleDiameterMm !== 'number' || g.innerHoleDiameterMm <= 0) {
    return '缺少或非法字段：innerHoleDiameterMm（必须 > 0 的数字）';
  }
  if (g.innerHoleDiameterMm >= g.outerHoleDiameterMm) {
    return '钥匙扣 innerHoleDiameterMm 必须小于 outerHoleDiameterMm';
  }
  return null;
}

/**
 * 按 POSITION 枚举或自定义点解析初始 centerPoint。
 * @param {string|null} position - POSITION.* 或 null
 * @param {Object} sharedState
 * @param {number} fallbackY - 找不到时的兜底 y
 * @returns {{x:number,y:number}}
 */
function resolvePositionToPoint(position, sharedState) {
  const canvasCenterX = Math.round(
    (sharedState.canvasWidth - sharedState.canvasRoundWhiteSpace * 2) / 2
  );
  let arr = [];
  if (position === POSITION.TOP_CENTER) {
    arr = sharedState.topHalfYArr;
  } else if (position === POSITION.BOTTOM_CENTER) {
    arr = sharedState.bottomHalfYArr;
  } else if (
    position === POSITION.MAX_TOP ||
    position === POSITION.MAX_BOTTOM
  ) {
    // 找极值点
    let extreme = sharedState.outlinePoints[0];
    const collected = [];
    sharedState.outlinePoints.forEach((v) => {
      if (position === POSITION.MAX_TOP) {
        if (v[1] - extreme[1] < 0) {
          extreme = v;
          collected.length = 0;
          collected.push(v);
        } else if (v[1] === extreme[1]) {
          collected.push(v);
        }
      } else if (position === POSITION.MAX_BOTTOM) {
        if (v[1] - extreme[1] > 0) {
          extreme = v;
          collected.length = 0;
          collected.push(v);
        } else if (v[1] === extreme[1]) {
          collected.push(v);
        }
      }
    });
    if (collected.length > 1) {
      collected.sort((a, b) => a[0] - b[0]);
      const midIndex = Math.floor(collected.length / 2);
      return { x: collected[midIndex][0], y: collected[midIndex][1] };
    }
    return { x: extreme[0], y: extreme[1] };
  }
  // TOP_CENTER / BOTTOM_CENTER：在 arr 中选 x 距 canvas 中线最近的点
  let minDelta;
  let best = null;
  arr.forEach((p) => {
    const v = Math.abs(p[0] - canvasCenterX);
    if (minDelta === undefined || v < minDelta) {
      minDelta = v;
      best = { x: p[0], y: p[1] };
    }
  });
  if (best) return best;
  // 兜底：轮廓任意点
  if (sharedState.outlinePoints.length) {
    return {
      x: sharedState.outlinePoints[0][0],
      y: sharedState.outlinePoints[0][1]
    };
  }
  return { x: 0, y: 0 };
}

/**
 * 计算半径（px）。
 * @param {Object} geometryConfig
 * @param {Object} sharedState
 * @returns {{outRadius:number,inRadius:number}}
 */
function computeRadii(geometryConfig, sharedState) {
  const k = sharedState.mmToPixelRatio / sharedState.maskFitScale;
  return {
    outRadius: (geometryConfig.outerHoleDiameterMm / 2) * k,
    inRadius: (geometryConfig.innerHoleDiameterMm / 2) * k
  };
}

/**
 * 以参考实例为基准，向右偏移 minGapPx + outRadius1 + outRadius2 作为新位置。
 * @param {Object} referenceInstance - 参考实例（最近选中或最右侧）
 * @param {number} outRadius - 新实例外圆半径
 * @param {number} minGapPx
 * @returns {{x:number,y:number}}
 */
function getNextRightOf(referenceInstance, outRadius, minGapPx) {
  return {
    x:
      referenceInstance.centerPoint.x +
      referenceInstance.outRadius +
      minGapPx +
      outRadius,
    y: referenceInstance.centerPoint.y
  };
}

/**
 * init：在 instance 上写入 outRadius / inRadius / centerPoint。
 * @param {Object} instance
 * @param {Object} sharedState
 * @param {Object} options
 * @param {Object} [options.placement] - { centerPoint:{x,y,baseCanvasWidth?} } 或 { position }
 * @param {Array<Object>} [options.sameTypeInstances]
 * @param {Object} [options.referenceInstance] - 同类型右偏移参考实例
 * @param {number} [options.minGapPx]
 * @returns {void}
 */
function init(instance, sharedState, options) {
  const radii = computeRadii(instance.geometryConfig, sharedState);
  instance.outRadius = radii.outRadius;
  instance.inRadius = radii.inRadius;

  const placement = (options && options.placement) || null;
  let centerPoint;
  if (placement && placement.centerPoint) {
    const cp = placement.centerPoint;
    // 按去除留白后的业务坐标宽度缩放，避免 padding 变化导致还原位置偏移。
    if (cp.baseCanvasWidth && cp.baseCanvasWidth !== sharedState.canvasWidth) {
      const k = getRestoreBusinessScale(cp, sharedState);
      centerPoint = { x: cp.x * k, y: cp.y * k };
    } else {
      centerPoint = { x: cp.x, y: cp.y };
    }
  } else {
    // 同类型已有实例时，在参考实例右侧偏移；否则用默认 TOP_CENTER
    const ref = (options && options.referenceInstance) || null;
    const minGapPx = (options && options.minGapPx) || 0;
    if (ref) {
      centerPoint = getNextRightOf(ref, instance.outRadius, minGapPx);
    }
    if (!centerPoint) {
      const position = (placement && placement.position) || POSITION.TOP_CENTER;
      centerPoint = resolvePositionToPoint(position, sharedState);
    }
  }
  instance.centerPoint = centerPoint;
}

/**
 * 把 centerPoint 收敛到合法位置；返回是否仍违规。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {boolean} true = 仍违规
 */
function clampSelf(instance, sharedState) {
  const points = sharedState.outlinePoints;
  if (!points || !points.length) return false;
  const closest = findClosestPoint(instance.centerPoint, points);
  if (!closest) return false;
  const dx = instance.centerPoint.x - closest.x;
  const dy = instance.centerPoint.y - closest.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const inside = isPointInPolygon(instance.centerPoint, points);
  if (inside) {
    if (dist > instance.outRadius) {
      const ratio = instance.outRadius / dist;
      instance.centerPoint.x = closest.x + dx * ratio;
      instance.centerPoint.y = closest.y + dy * ratio;
    }
  } else {
    if (dist > instance.inRadius) {
      const ratio = instance.inRadius / dist;
      instance.centerPoint.x = closest.x + dx * ratio;
      instance.centerPoint.y = closest.y + dy * ratio;
    }
  }
  // 上限 1 个 + 双圆相切约束已被自动收敛 → 不算违规
  return false;
}

/**
 * 命中检测：点是否在外圆内。
 * @param {Object} instance
 * @param {{x:number,y:number}} point
 * @returns {boolean}
 */
function hitTest(instance, point) {
  const dx = point.x - instance.centerPoint.x;
  const dy = point.y - instance.centerPoint.y;
  return dx * dx + dy * dy <= instance.outRadius * instance.outRadius;
}

/**
 * 获取编辑态命中信息；小尺寸钥匙扣补足到最小热区。
 * @param {Object} instance
 * @param {{x:number,y:number}} point
 * @returns {{hit:boolean,isRealHit:boolean,distance:number}}
 */
function getHitTestInfo(instance, point) {
  const dx = point.x - instance.centerPoint.x;
  const dy = point.y - instance.centerPoint.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const realRadius = instance.outRadius;
  const hitRadius = Math.max(
    realRadius + DEFAULT_CONFIG.componentLargeHitPaddingPx,
    DEFAULT_CONFIG.componentMinHitSizePx / 2
  );
  return {
    hit: distance <= hitRadius,
    isRealHit: distance <= realRadius,
    distance
  };
}

/**
 * 拖动开始（暂无额外副作用）。
 */
function onDragStart(instance, point /* , sharedState */) {
  instance._dragOffset = {
    dx: point.x - instance.centerPoint.x,
    dy: point.y - instance.centerPoint.y
  };
}

/**
 * 拖动中：把 centerPoint 设为光标对应位置 - 起手偏移；clampSelf 自动收敛。
 */
function onDragMove(instance, point, sharedState) {
  const off = instance._dragOffset || { dx: 0, dy: 0 };
  instance.centerPoint.x = point.x - off.dx;
  instance.centerPoint.y = point.y - off.dy;
  clampSelf(instance, sharedState);
}

function onDragEnd(instance /* , sharedState */) {
  instance._dragOffset = null;
}

/**
 * 编辑模式：外/内圆 + 内圆中心（与 serviceHoleNew/renderer.js#drawKeyHoleRound 风格一致）。
 */
function drawEdit(instance, ctx, sharedState) {
  ctx.save();
  ctx.strokeStyle = roundColor;
  ctx.lineWidth = sharedState.lineWidth;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(
    instance.centerPoint.x,
    instance.centerPoint.y,
    instance.inRadius,
    0,
    Math.PI * 2
  );
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(
    instance.centerPoint.x,
    instance.centerPoint.y,
    instance.outRadius,
    0,
    Math.PI * 2
  );
  ctx.stroke();
  ctx.restore();
}

/**
 * 预览模式（效果图）：外圆替换轮廓被孔位裁掉的部分 + 内圆。
 * 简化实现：把外圆与刀线重叠的两个轮廓点之间用圆弧替代。
 */
function drawPreview(instance, ctx, sharedState) {
  const points = sharedState.outlinePoints;
  if (!points || !points.length) return;

  // 找 outRadius 进入点 / 退出点（与 serviceHoleNew 一致）
  const inPoints = [];
  const outPoints = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const prevIdx = i === 0 ? points.length - 1 : i - 1;
    const prev = points[prevIdx];
    const dCur = Math.hypot(
      p[0] - instance.centerPoint.x,
      p[1] - instance.centerPoint.y
    );
    const dPrev = Math.hypot(
      prev[0] - instance.centerPoint.x,
      prev[1] - instance.centerPoint.y
    );
    const curIn = dCur <= instance.outRadius;
    const prevIn = dPrev <= instance.outRadius;
    if (!prevIn && curIn) inPoints.push({ index: i, point: p });
    else if (prevIn && !curIn) outPoints.push({ index: i, point: p });
  }

  ctx.save();
  ctx.strokeStyle = sharedState.lineColor || lineColor;
  ctx.lineWidth = sharedState.lineWidth;

  // 圆弧补孔位边缘（孔内刀线点已在 outlineRenderer.drawOutline 中过滤）
  if (inPoints.length > 0 && outPoints.length > 0) {
    const inPt = inPoints[0];
    const outPt = outPoints[outPoints.length - 1];
    const center = instance.centerPoint;
    const startAngle = Math.atan2(
      outPt.point[1] - center.y,
      outPt.point[0] - center.x
    );
    const endAngle = Math.atan2(
      inPt.point[1] - center.y,
      inPt.point[0] - center.x
    );
    ctx.beginPath();
    ctx.arc(center.x, center.y, instance.outRadius, startAngle, endAngle);
    ctx.stroke();
  }

  // 内圆（红色实心圈）
  ctx.beginPath();
  ctx.arc(
    instance.centerPoint.x,
    instance.centerPoint.y,
    instance.inRadius,
    0,
    Math.PI * 2
  );
  ctx.stroke();
  ctx.restore();
}

/**
 * 碰撞形状：用外圆参与冲突检测（spec §3.3）。
 */
function getCollisionShape(instance) {
  return {
    kind: 'circle',
    cx: instance.centerPoint.x,
    cy: instance.centerPoint.y,
    r: instance.outRadius
  };
}

/**
 * 选中/冲突外接矩形（覆盖外圆 + 一点 padding）。
 */
function getSelectionRect(instance) {
  const r = instance.outRadius + 1;
  return {
    x: instance.centerPoint.x - r,
    y: instance.centerPoint.y - r,
    w: r * 2,
    h: r * 2
  };
}

/**
 * 放大镜锚点。
 */
function getMagnifierAnchor(instance) {
  return { x: instance.centerPoint.x, y: instance.centerPoint.y };
}

/**
 * 导出 layerData 片段。
 * @param {Object} instance
 * @param {Object} sharedState
 * @returns {{keyPointList:Array, originCenterPointList:Array}}
 */
function toExport(instance, sharedState) {
  const backendPoint = convertCanvasToBackend(
    instance.centerPoint,
    sharedState
  );
  const keyPoint = [
    +(backendPoint.x / sharedState.imageInfo.width).toFixed(6),
    +(backendPoint.y / sharedState.imageInfo.height).toFixed(6)
  ];
  const originItem = {
    x: instance.centerPoint.x,
    y: instance.centerPoint.y,
    baseCanvasWidth: sharedState.canvasWidth,
    canvasRoundWhiteSpace: sharedState.canvasRoundWhiteSpace,
    componentId: instance.id,
    geometryConfig: instance.geometryConfig,
    sizePresetId: instance.sizePresetId || '',
    enableMagnifier: instance.enableMagnifier !== false
  };
  return {
    keyPointList: [keyPoint],
    originCenterPointList: [originItem]
  };
}

/**
 * dirty 比较：只关心 centerPoint（mm 配置和元数据不变时不该判 dirty）。
 */
function serializeForDirty(instance) {
  return {
    cx: Math.round(instance.centerPoint.x * 100) / 100,
    cy: Math.round(instance.centerPoint.y * 100) / 100
  };
}

/** 策略对象 */
const keyHoleStrategy = {
  type: DRAW_TYPE.KEY_HOLE,
  validateGeometryConfig,
  init,
  clampSelf,
  hitTest,
  getHitTestInfo,
  onDragStart,
  onDragMove,
  onDragEnd,
  drawEdit,
  drawPreview,
  getCollisionShape,
  getSelectionRect,
  getMagnifierAnchor,
  toExport,
  serializeForDirty
};

registerComponentType(keyHoleStrategy);

export default keyHoleStrategy;
